<script setup lang="ts">
const router = useRouter()

// These paths immediately redirect. Going back to one can reopen the same screen.
const redirects = new Set([
  '/manager',
  '/crm',
  '/crm/leads/import',
  '/scheduling',
  '/scheduling/weekly-schedule',
  '/scheduling/crews-route',
])

function isWorkspacePage(location: string) {
  if (!location.startsWith('/') || location.startsWith('//')) return false
  const target = router.resolve(location)
  return (
    !redirects.has(target.path.replace(/\/$/, '')) &&
    target.matched.some((record) => record.meta.middleware === 'authenticated')
  )
}

const destination = computed(() => {
  const { path, fullPath } = router.currentRoute.value
  const previous = router.options.history.state.back
  if (
    typeof previous === 'string' &&
    previous !== fullPath &&
    isWorkspacePage(previous)
  ) {
    return { path: previous, fromHistory: true }
  }

  // A new tab or an external link has no previous workspace entry.
  const segments = path.split('/').filter(Boolean)
  while (segments.length > 1) {
    segments.pop()
    const parent = '/' + segments.join('/')
    if (isWorkspacePage(parent)) return { path: parent, fromHistory: false }
  }
  return fullPath === '/' ? null : { path: '/', fromHistory: false }
})

function goBack() {
  const target = destination.value
  if (!target) return
  if (target.fromHistory) router.back()
  else return router.replace(target.path)
}
</script>

<template>
  <Button
    type="button"
    icon="pi pi-arrow-left"
    label="Back"
    severity="secondary"
    rounded
    outlined
    class="min-h-[44px] shrink-0 bg-surface-0 dark:bg-surface-900"
    :disabled="!destination"
    @click="goBack"
  />
</template>
